// src/components/achievements/AchievementRanking.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import NavLink from './../common/NavLink';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const AchievementRanking = () => {
  const { category } = useParams();
  const [metadata, setMetadata] = useState({
    title: 'Ranking',
    color: 'white',
    mainColumnName: null,
    extraColumns: [] // zawsze tablica
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/achievements/${category}?limit=100`)
      .then(res => res.json())
      .then(result => {
        // obsługa extraColumns w formacie JSONB z PostgreSQL
        let extraCols = [];
        if (result.metadata?.extraColumns) {
          if (Array.isArray(result.metadata.extraColumns)) {
            extraCols = result.metadata.extraColumns;
          } else if (result.metadata.extraColumns.value) {
            try {
              extraCols = JSON.parse(result.metadata.extraColumns.value);
            } catch (e) {
              console.warn('Błąd parsowania extraColumns', e);
            }
          }
        }

        setMetadata({
          title: result.metadata?.title || 'Ranking',
          color: result.metadata?.color || 'white',
          mainColumnName: result.metadata?.mainColumnName || null,
          extraColumns: extraCols
        });
        setData(result.data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Błąd pobierania rankingu:', err);
        setLoading(false);
      });
  }, [category]);

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><Typography>Ładowanie...</Typography></Box>;
  if (data.length === 0) return <Box sx={{ textAlign: 'center', py: 8 }}><Typography>Brak danych</Typography></Box>;

  const { title, color, mainColumnName, extraColumns } = metadata;

  // Główna kolumna wartości
  const mainValueKey = Object.keys(data[0] || {}).find(key =>
    !['rank', 'teamId', 'teamName', 'leagueName', 'leagueGroup', 'matches', 'competitionId'].includes(key)
  );

  const mainColumnTitle = mainColumnName ||
    (mainValueKey ? mainValueKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '');

  const getColorValue = () => {
    if (color === 'green') return '#4caf50';
    if (color === 'red') return '#f44336';
    return '#ffffff';
  };

  const hasMatches = data.some(row => row.matches != null);

  return (
    <Box sx={{ maxWidth: { xs: '100%', md: 900 }, mx: 'auto', px: { xs: 0, md: 2 }, py: 1 }}>
      <Typography variant="h5" align="center" gutterBottom sx={{ mb: 4, fontWeight: 700, color: '#fff' }}>
        {title}
      </Typography>

      <TableContainer component={Paper} sx={{ bgcolor: '#1a1a1a', borderRadius: 2, overflow: 'hidden' }}>
        <Table size="dense" sx={{ '& .MuiTableRow-root': { height: { xs: 38, md: 45 } } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#111' }}>
              <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' }, width: 30 }}>
                #
              </TableCell>
              <TableCell sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                Drużyna
              </TableCell>
              <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' }, width: 160 }}>
                Liga
              </TableCell>
              {hasMatches && (
                <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' }, width: 50 }}>
                  M
                </TableCell>
              )}
              {mainValueKey && (
                <TableCell align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                  {mainColumnTitle}
                </TableCell>
              )}
              {extraColumns.map((col, idx) => (
                <TableCell key={idx} align="center" sx={{ color: '#aaa', fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                  {col.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => {
              // Poprawne wyświetlanie ligi – bez pustych nawiasów
              const leagueDisplay = row.leagueGroup && row.leagueGroup !== 'Brak' && row.leagueGroup.trim() !== ''
                ? `${row.leagueName} (${row.leagueGroup})`
                : row.leagueName;

              return (
                <TableRow key={row.teamId} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                  <TableCell align="center">
                    <Typography fontWeight={700} color="#fff" sx={{ fontSize: { xs: '0.7rem', md: '0.95rem' } }}>
                      {row.rank}.
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography
                      component={NavLink}
                      to={`/team/${row.teamId}`}
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: { xs: '0.7rem', md: '0.9rem' },
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {row.teamName}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Typography
                      component={NavLink}
                      to={`/liga/${row.competitionId}`}
                      sx={{
                        color: '#bbbbbb',
                        fontSize: { xs: '0.6rem', md: '0.85rem' },
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {leagueDisplay}
                    </Typography>
                  </TableCell>

                  {hasMatches && (
                    <TableCell align="center">
                      <Typography fontWeight={600} color="#ccc" sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' } }}>
                        {row.matches ?? '-'}
                      </Typography>
                    </TableCell>
                  )}

                  {mainValueKey && (
                    <TableCell align="center">
                      <Typography
                        fontWeight={900}
                        sx={{
                          fontSize: { xs: '0.9rem', md: '1.1rem' },
                          color: getColorValue(),
                          textShadow: color === 'green' ? '0 0 6px rgba(76,175,80,0.4)' :
                                      color === 'red' ? '0 0 6px rgba(244,67,54,0.4)' : 'none',
                        }}
                      >
                        {row[mainValueKey]}
                      </Typography>
                    </TableCell>
                  )}

                  {extraColumns.map((col, idx) => (
                    <TableCell key={idx} align="center">
                      <Typography fontWeight={500} color="#ccc" sx={{ fontSize: { xs: '0.65rem', md: '0.9rem' } }}>
                        {row[col.key] != null ? row[col.key] : '-'}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AchievementRanking;